<template>
    <div
        ref="modalRef"
        tabindex="-1"
        :id="id"
        :style="{ display: modelValue ? 'block' : 'none' }"
        :class="{'show active-modal': modelValue, 'sibling-backdrop': backdrop }"
        @click="backdropClicked" class="modal fade"
    >
        <div
            class="modal-dialog"
            role="document"
            :class="dynamicClasses"
            :style="stylesObj"
        >
            <div class="modal-content" :class="{ 'pe-6 ps-6': navigation, 'boxed': useBoxedModal }">
                <div v-if="showHeader" class="modal-header" :style="headerStylesObj">
                    <slot name="header" />
                    <button v-if="closeButton === true" type="button" class="btn-close" @click="closeModal" :style="{ fontSize: closeButtonSize }" />
                </div>

                <div class="modal-body">
                    <slot />
                </div>

                <div v-if="$slots.footer" class="modal-footer">
                    <slot name="footer" />
                </div>
                <template v-if="navigation">
                    <div @click="goNext" class="navigation right">
                        <div class="nav-container">
                            <AppIcon :icon="faUser" />
                            <i class="bi bi-chevron-right"></i>
                        </div>
                    </div>
                    <div @click="goBack" class="navigation left">
                        <div class="nav-container">
                            <i class="bi bi-chevron-left"></i>
                            <AppIcon :icon="faUser" />
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { faUser } from "@fortawesome/free-regular-svg-icons";
import AppIcon from "@/vue/shared/AppIcon.vue";

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
    id: {
        type: String,
        default: () => 'app-modal-' + Math.random().toString(36).slice(2, 9),
    },
    // https://dev.portal.reacthealth.com/theme/build/documentation/modal.html#sizes
    size: {
        type: String,
        default: () => "md",
        validator: (value) => [
            "sm", "md", "lg", "xl",
            /* customs */
            "l-md"
        ].includes(value)
    },
    onClose: {
        type: Function,
        default: null
    },
    closeButton: {
        type: Boolean,
        default: true
    },
    showHeader: {
        type: Boolean,
        default: true
    },
    backdrop: {
        type: Boolean,
        default: false
    },
    navigation: {
        type: Boolean,
        default: false
    },
    containerStyles: {
        type: Object,
        default: {}
    },
    scrollable: {
        type: Boolean,
        default: false
    },
    headerStyles: {
        type: Object,
        default: {}
    },
    closeButtonSize: {
        type: String,
        default: "18px"
    }
});

const emits = defineEmits(['update:modelValue', 'clickNext', 'clickBack']);

const instance = ref();
const modalRef = ref();
const modalBackdrop = ref(null);
const useBoxedModal = ref(false);
const dynamicClasses = computed(() => {
    const sizeClass = props.size ? `modal-${props.size}` : '';
    const scrollableClass = props.scrollable ? 'modal-dialog-scrollable' : '';
    return `${sizeClass} ${scrollableClass}`;
});
const stylesObj = computed(() => {
    return props.containerStyles;
});

const
    headerStylesObj = computed(() => {
    return props.headerStyles;
});

const closeModal = async () => {
    const hasCustomHandler = typeof props.onClose === "function";

    if (!hasCustomHandler) {
        hide();

        return;
    }

    try {
        const shouldClose = await props.onClose();

        if (shouldClose) {
            hide();
        }
    } catch (error) {
        console.error("Error during onClose handler:", error);
    }
};

const toggle = () => {
    const parentModal = modalRef.value.closest(".active-modal");

    if (props.modelValue) {
        const backdropDiv = document.createElement("div");

        backdropDiv.classList.add("modal-backdrop");
        backdropDiv.classList.add("fade");
        backdropDiv.classList.add("show");

        modalBackdrop.value = backdropDiv;

        if (parentModal === null) {
            const body = document.getElementsByTagName("body")[0];

            body.append(backdropDiv);

            return;
        }

        parentModal.append(backdropDiv);
        useBoxedModal.value = true;
        return;
    }

    modalBackdrop?.value?.remove();
};

const hide = () => emits('update:modelValue', false);

const goNext = () => {
    emits('clickNext');
}

const goBack = () => {
    emits('clickBack');
}

const backdropClicked = (e) => {
    if (modalRef.value === e.target) {
        // closeModal(); // removed for card #4658 leaving code in case we need to revert
    }
}

watch(
    () => props.modelValue,
    () => toggle(),
);

defineExpose({
    closeModal,
    hide
});

</script>
<style scoped>
.nav-container {
    display: flex;
    align-items: center;
    height: 100%;
}
.navigation {
    position: absolute;
    justify-content: center;
    height: 30%;
    align-items: baseline;
    display: flex;
    width: 50px;
    cursor: pointer;
}
.navigation:hover {
    background: #d3d3d329;
}
.navigation.right {
    right: 0px;
}
.navigation i {
    font-size: 30px;
}
.navigation.left {
    left: 0px;
}
.modal.modal-level-2 {
    --bs-modal-zindex: 200;
}
.modal.modal-level-3 {
    --bs-modal-zindex: 300;
}
.modal.modal-level-4 {
    --bs-modal-zindex: 400;
}
.modal-content.boxed {
    box-shadow: rgba(0, 0, 0, 0.25) 0px 54px 55px, rgba(0, 0, 0, 0.12) 0px -12px 30px, rgba(0, 0, 0, 0.12) 0px 4px 6px, rgba(0, 0, 0, 0.17) 0px 12px 13px, rgba(0, 0, 0, 0.09) 0px -3px 5px;
}
.sibling-backdrop {
    z-index: 100;
}
.modal-l-md {
    max-width: 590px;
}
</style>
